import React, { FC, useContext } from 'react';
import { Redirect, RouteComponentProps, StaticContext } from 'react-router';
import { useQuery } from '@apollo/react-hooks';

import {
  AnimatedScreen,
  HRule,
  LoadingPage,
  UserStatusPill,
} from 'Shared/components';
import { isPresent } from 'Shared/utils';

import { NetworkError, NpnStatusBanner } from 'Admin/components';
import { UserType } from 'Admin/Users/types';
import { GET_USER_BY_ID } from 'Admin/Users/graphql';
import { PartnerContext } from 'Admin/utils';

import { UpdateUserForm } from './UpdateUserForm';
import styles from './styles.module.css';

export const UserDetails: FC<RouteComponentProps<
  {
    userId: string;
    partnerId?: string;
  },
  StaticContext,
  { refreshData: boolean }
>> = ({ match: { params }, location }) => {
  const { loading: loadingPartner, currentPartner } = useContext(
    PartnerContext
  );

  const { error, loading, data, refetch } = useQuery<{
    portalUser: UserType;
  }>(GET_USER_BY_ID, {
    variables: {
      id: params.userId,
    },
  });

  if (loadingPartner || loading) return <LoadingPage />;
  const user = data && data.portalUser;

  if (!user) {
    // handle network errors
    if (error?.networkError) {
      return <NetworkError />;
    } else {
      // handle other errors with message
      return (
        <div>
          Something went wrong retrieving {params.userId}: {error?.message}
        </div>
      );
    }
  }

  if (
    isPresent(user.partner) &&
    !location.state?.refreshData &&
    (!currentPartner || user.partner.id !== currentPartner.id)
  ) {
    // Users associated with a partner should only be opened in that partner's context
    // When a user is moved from one partner to another, we pass refreshData in location state
    // to bypass this redirect and re-request the user data from the server
    return (
      <Redirect to={`/admin/partners/${user.partner.id}/users/${user.id}`} />
    );
  } else if (!user?.partner && currentPartner) {
    // Users NOT associated with a partner should only be opened in a system context
    return <Redirect to={`/admin/users/${user.id}`} />;
  }

  if (location.state?.refreshData && user.partner?.id !== currentPartner?.id) {
    refetch({
      variables: {
        id: params.userId,
      },
    });
  }

  return (
    <AnimatedScreen
      testId="user-details"
      className={styles.container}
      error={error && error.message}
    >
      <NpnStatusBanner npnStatus={user.npnStatus} />
      <div className={styles.pageHeader}>
        <h1 data-testid="user-details-header">
          {user.firstName} {user.lastName}
        </h1>
        <UserStatusPill activated={user.activated} />
      </div>
      <HRule size="lg" />
      <UpdateUserForm user={user} currentPartner={currentPartner} />
    </AnimatedScreen>
  );
};
